import React, { useCallback, useEffect, useState } from 'react'
import { Sparkles, Loader2, X } from 'lucide-react'
import agentService, { AgentF1GridProposal } from '../../services/agentService'
import { useTranslation } from '../../hooks/use-i18n'

interface F1GridCopilotPanelProps {
  roomId: string
  raceId: string
  onApplyProposal: (proposal: AgentF1GridProposal) => void
}

const F1GridCopilotPanel: React.FC<F1GridCopilotPanelProps> = ({
  roomId,
  raceId,
  onApplyProposal,
}) => {
  const { t, locale } = useTranslation()
  const [enabled, setEnabled] = useState<boolean | null>(null)
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [proposal, setProposal] = useState<AgentF1GridProposal | null>(null)
  const [sources, setSources] = useState<string[]>([])
  const [fromTemplate, setFromTemplate] = useState(false)
  const [question, setQuestion] = useState('')

  useEffect(() => {
    let cancelled = false
    agentService
      .getStatus()
      .then((s) => {
        if (!cancelled) setEnabled(s.enabled)
      })
      .catch(() => {
        if (!cancelled) setEnabled(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const fetchCopilot = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await agentService.f1GridCopilot({
        roomId,
        raceId,
        question: question.trim() || undefined,
        locale,
      })
      setMessage(res.message)
      setProposal(res.proposal ?? null)
      setSources(res.sources ?? [])
      setFromTemplate(res.fromTemplate)
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('agent.errorGeneric')
      setError(typeof msg === 'string' ? msg : t('agent.errorGeneric'))
    } finally {
      setLoading(false)
    }
  }, [roomId, raceId, question, locale, t])

  const handleOpen = () => {
    setOpen(true)
    if (!message && !loading) void fetchCopilot()
  }

  if (enabled === false) return null

  if (!open) {
    return (
      <button
        type="button"
        onClick={handleOpen}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-primary/10 text-primary text-sm font-semibold hover:bg-primary/15 transition-colors mb-4"
      >
        <Sparkles className="w-4 h-4" />
        {t('agent.f1AskCopilot')}
      </button>
    )
  }

  return (
    <div className="rounded-xl border border-border bg-muted/20 p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <span className="font-semibold text-foreground">{t('agent.f1CopilotTitle')}</span>
          <span className="text-[10px] uppercase tracking-wide text-primary font-semibold">{t('agent.aiBadge')}</span>
        </div>
        <button type="button" onClick={() => setOpen(false)} aria-label={t('agent.close')}>
          <X className="w-5 h-5 text-muted-foreground" />
        </button>
      </div>

      <div className="flex gap-2 mb-3">
        <input
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder={t('agent.f1QuestionPlaceholder')}
          className="flex-1 px-3 py-2 rounded-lg bg-background border border-border text-sm"
        />
        <button
          type="button"
          onClick={() => void fetchCopilot()}
          disabled={loading}
          className="btn-primary px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
        >
          {t('agent.ask')}
        </button>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t('agent.loading')}
        </div>
      )}
      {error && <p className="text-sm text-destructive py-2">{error}</p>}
      {message && !loading && (
        <div className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{message}</div>
      )}
      {fromTemplate && message && (
        <p className="text-xs text-muted-foreground mt-2">({t('agent.templateFallback')})</p>
      )}
      {sources.length > 0 && (
        <p className="text-xs text-muted-foreground mt-2">
          {t('agent.sources')}: {sources.join(' · ')}
        </p>
      )}
      {proposal && (
        <button
          type="button"
          onClick={() => onApplyProposal(proposal)}
          className="mt-3 btn-primary px-4 py-2 rounded-lg text-sm font-semibold"
        >
          {t('agent.f1ApplyGrid')}
        </button>
      )}
    </div>
  )
}

export default F1GridCopilotPanel
